/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Plus, FileText, X } from 'lucide-react';

interface LinkDialogProps {
  show: boolean;
  linkInputValue: string;
  onLinkInputChange: (value: string) => void;
  onAddFromInput: () => void;
  onAddFromFile: () => void;
  onCancel: () => void;
}

export function LinkDialog({
  show,
  linkInputValue,
  onLinkInputChange,
  onAddFromInput,
  onAddFromFile,
  onCancel
}: LinkDialogProps) {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Link</h3>
        
        <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
          Enter a URL or file path (e.g., <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '12px' }}>tasks/plan.md</code> or <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '12px' }}>https://example.com</code>):
        </p>
        <p style={{ marginBottom: '16px', color: '#28a745', fontSize: '12px', fontStyle: 'italic' }}>
          💡 Tip: The file browser will automatically search for the file in your codebase and use the correct path when found.
        </p>
        
        <input
          type="text"
          value={linkInputValue}
          onChange={(e) => onLinkInputChange(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onAddFromInput();
            }
          }}
          placeholder="https://example.com or tasks/plan.md"
          autoFocus
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            marginBottom: '16px',
            boxSizing: 'border-box'
          }}
        />
        
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginTop: '20px',
          alignItems: 'center'
        }}>
          <button
            onClick={onAddFromInput}
            disabled={!linkInputValue.trim()}
            className="btn"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: linkInputValue.trim() ? 1 : 0.5,
              cursor: linkInputValue.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            <Plus size={16} />
            Add Link
          </button>
          
          <span style={{ color: '#999' }}>or</span>
          
          <button
            onClick={onAddFromFile}
            className="btn"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              backgroundColor: '#6c757d',
              borderColor: '#6c757d'
            }}
          >
            <FileText size={16} />
            Browse Files
          </button>
        </div>
        
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={onCancel}
            className="btn btn-secondary"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <X size={16} />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

